import {
  Component, Input, OnInit, OnDestroy, ViewChild, Output, EventEmitter,
  ChangeDetectionStrategy, ChangeDetectorRef,
} from '@angular/core';
import { FormBuilder, FormGroup, FormArray, FormControl, AbstractControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { merge } from 'rxjs';
import * as _ from 'lodash';
import * as moment from 'moment';



import { rightPanelNavs } from './right-panel-navs';
import { fieldsInfo } from './digital-assets-form/fields-info';
import { digitalAssetsSegmentData } from '../digital-asset-details/digital-assets-segment-data';
import { getDamHintsDefaultHTML } from '../digital-asset-details/hints-default-html';
import { digitalAssetErrors } from 'shared/digital-assets/digital-asset-errors';
import { getAssetThumbnail } from 'shared/digital-assets/digital-asset-helpers';
import { setUnreadCommentsCount } from 'shared/comments/comments-helpers';

import { PanelModalComponent } from 'shared/modal/panel-modal/panel-modal.component';
import { ErrorModalComponent } from 'shared/modal/error-modal/error-modal.component';
import { DynamicNavsComponent } from 'shared/nav/dynamic-navs/dynamic-navs.component';
import { DigitalAssetsFormComponent } from './digital-assets-form/digital-assets-form.component';

import { BrandService } from 'shared/brand/brand.service';
import { DigitalAssetsService } from 'shared/digital-assets/digital-assets.service';
import { SegmentValidationService } from 'shared/validation/segment-validation/segment-validation.service';
import { HintsService } from 'shared/hints/hints.service';
import { CommentsService } from 'shared/comments/comments.service';
import { PreferencesService } from 'shared/preferences/preferences.service';
import { ConvertUTCToTZPipe } from 'shared/pipes/convert-utc-to-tz.pipe';
import {finalize, takeWhile} from 'rxjs/operators';
import {ProductPanelComponent} from '../product-panel/product-panel.component';


@Component({
  moduleId: module.id,
  selector: 'digital-assets-panel',
  templateUrl: 'digital-assets-panel.component.html',
  styleUrls: ['digital-assets-panel.component.scss'],
  providers: [HintsService, SegmentValidationService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DigitalAssetsPanelComponent implements OnInit, OnDestroy {

  @ViewChild(PanelModalComponent, {static: true})
  panelModal: PanelModalComponent | void;

  @ViewChild(DigitalAssetsFormComponent, {static: false})
  digitalAssetsForm: DigitalAssetsFormComponent | void;

  @ViewChild(ErrorModalComponent, {static: false})
  errorModal: ErrorModalComponent | void;

  @ViewChild(DynamicNavsComponent, {static: true})
  dynamicNavs: DynamicNavsComponent | void;

  @Output() closePanel  = new EventEmitter();

  data: any;
  segmentData = digitalAssetsSegmentData;

  rightPanelNavs: any = rightPanelNavs;
  errorModalTitle: string = '';
  saving: boolean = false;
  uploadedDate: string;
  imageUrl: string;
  originId: string;

  private isPageActive: boolean = true;
  private sourceData: any;
  private defaultHintFields: Array<string> = [
    'file_type',
    'filesize',
  ];
  private timezone: any;

  get isDirty(): boolean {
    if (!this.digitalAssetsForm || !this.digitalAssetsForm.form) { return false; }
    if (!this.digitalAssetsForm.form.get('filename').valid) { return false; }
    return this.digitalAssetsForm.form.dirty;
  }

  get isInvalid(): boolean {
    return this.digitalAssetsForm && this.digitalAssetsForm.form && this.digitalAssetsForm.form.invalid;
  }

  get formData(): boolean {
    return this.digitalAssetsForm && this.digitalAssetsForm.form.value;
  }

  get brandId(): string {
    return this.brandService.getBrand();
  }

  get itemId(): any {
    return _.get(this.route.snapshot.params, 'id', '');
  }

  constructor(
    private brandService: BrandService,
    private digitalAssetsService: DigitalAssetsService,
    private hintsService: HintsService,
    private commentsService: CommentsService,
    private route: ActivatedRoute,
    private ref: ChangeDetectorRef,
    private preferencesService: PreferencesService,
    private convertUTCToTZPipe: ConvertUTCToTZPipe,
  ) {}

  ngOnInit() {
    if (this.panelModal) {
      this.panelModal.onShown
        .pipe(takeWhile(() => this.isPageActive))
        .subscribe(() => {
          this.subscribeAssetDetails();
        });
      this.panelModal.onHidden
        .pipe(takeWhile(() => this.isPageActive))
        .subscribe(() => {
          if (this.dynamicNavs) {
            this.dynamicNavs.selectNav('hints');
          }
        });
    }

    _.defer(() => {
      this.subscribeNavsChange();
    });

    this.preferencesService.getPreferences()
    .subscribe((preferences: any) => {
      this.timezone = preferences.timezone;
    });
  }

  ngOnDestroy() {
    this.isPageActive = false;
    this.sourceData = {};
    this.data = {};
    this.segmentData = [];
    this.rightPanelNavs = [];
  }

  // handlers
  onSaveClick($event: any) {
    if (this.digitalAssetsForm) {
      const fileNameControl = this.digitalAssetsForm.form.get('filename');
      if (fileNameControl.invalid && _.has(fileNameControl.errors, 'unique')) {
        this.errorModalTitle = digitalAssetErrors.filenameUniq;
        if (this.errorModal) {
          this.errorModal.show();
        }
        return;
      }
    }
    this.saving = true;
    this.digitalAssetsService.putAssets(this.brandId, this.itemId, this.formData)
    .pipe(finalize(() => this.saving = false))
    .subscribe(() => {
      this.subscribeAssetDetails();
    });
  }

  onCancelClick($event: any) {
    if (this.isDirty) {
      this.data = this.sourceData;
    } else {
      this.hide();
    }
  }

  // public
  show() {
    if (this.panelModal) {
      this.panelModal.show();
    }
  }

  hide() {
    this.closePanel.emit();
    if (this.panelModal) {
      this.panelModal.hide();
    }
  }

  // private
  private subscribeAssetDetails(): void {
    this.digitalAssetsService.fetchAssets(this.brandId, this.itemId).subscribe((data: any) => {
      this.setData(data);
    });
  }

  private setData(data: any): void {
    this.data = data;
    this.sourceData = {...data};
    this.uploadedDate = this.getUploadedDate();
    this.imageUrl = this.getImageUrl();
    this.originId = _.get(data, 'origin_id');
    this.setUnreadCommentsCount();
    _.defer(() => {
      this.ref.markForCheck();
    });
  }

  private getUploadedDate(): string {
    if (!this.data || !this.data.created_at) { return ''; }
    const dateTZ = this.convertUTCToTZPipe.transform(this.data.created_at, this.timezone);
    return 'Uploaded ' + moment(dateTZ).format('MMMM Do, YYYY');
  }

  private getImageUrl(): string {
    return this.data ? getAssetThumbnail(this.data, 'small') : '';
  }

  private subscribeNavsChange() {
    if (this.dynamicNavs) {
      this.dynamicNavs.changeNav
        .pipe(takeWhile(() => this.isPageActive))
        .subscribe((obj: any) => {
          if (obj.nav.name === 'comments') {
            obj.componentRef.instance.fetch(this.brandId, this.originId, 'asset');
            merge(
              obj.componentRef.instance.commentMarkedAsRead,
              obj.componentRef.instance.commentResolved
            )
              .pipe(takeWhile(() => obj.componentRef.instance.isActive))
              .subscribe(() => {
                this.setUnreadCommentsCount();
              });
          }
        });
    }
  }

  private setUnreadCommentsCount(): void {
    this.commentsService.getUnreadCount(this.brandId, 'asset', this.originId)
    .subscribe((data: any) => {
      setUnreadCommentsCount(this.rightPanelNavs, data.count);
      this.ref.markForCheck();
    });
  }
}
